(() => {
  const elementName = 'neat-announcement-urgency';

  if (customElements.get(elementName)) return;

  class NeatAnnouncementUrgency extends HTMLElement {
    constructor() {
      super();
      this.onScroll = this.onScroll.bind(this);
      this.onResize = this.onResize.bind(this);
      this.onSectionSelect = this.onSectionSelect.bind(this);
      this.onSectionDeselect = this.onSectionDeselect.bind(this);
    }

    connectedCallback() {
      this.urgencyBar = this.querySelector('[data-urgency-bar]');
      if (!this.urgencyBar) return;

      this.sectionId = this.dataset.sectionId;
      this.scrollTrigger = Number.parseInt(this.dataset.scrollTrigger || '0', 10);
      this.countdownEnabled = this.dataset.countdownEnabled === 'true';
      this.countdownMode = this.dataset.countdownMode || 'rolling';
      this.countdownDuration = Math.max(Number.parseInt(this.dataset.countdownDuration || '300', 10), 1);
      this.expiryBehaviour = this.dataset.expiryBehaviour || 'hide';
      this.deadline = null;
      this.countdownOutput = this.querySelector('[data-countdown-output]');
      this.countdownAccessible = this.querySelector('[data-countdown-accessible]');
      this.editorPreview = false;
      this.isEligible = !this.countdownEnabled;
      this.isExpired = false;

      if (this.countdownEnabled) {
        this.deadline = this.countdownMode === 'rolling'
          ? this.getRollingDeadline()
          : Date.parse(this.dataset.deadline || '');

        if (Number.isFinite(this.deadline)) {
          this.isEligible = true;
          this.updateCountdown();
          this.countdownInterval = window.setInterval(() => this.updateCountdown(), 1000);
        } else if (window.Shopify?.designMode) {
          this.isEligible = true;
          this.classList.add('has-config-warning');
        }
      }

      if (this.isEligible) this.urgencyBar.hidden = false;

      window.addEventListener('scroll', this.onScroll, { passive: true });
      window.addEventListener('resize', this.onResize, { passive: true });

      if (window.Shopify?.designMode) {
        document.addEventListener('shopify:section:select', this.onSectionSelect);
        document.addEventListener('shopify:section:deselect', this.onSectionDeselect);
      }

      if ('ResizeObserver' in window) {
        this.resizeObserver = new ResizeObserver(() => this.updateHeight());
        this.resizeObserver.observe(this.urgencyBar);
      }

      this.updateVisibility();
    }

    disconnectedCallback() {
      window.removeEventListener('scroll', this.onScroll);
      window.removeEventListener('resize', this.onResize);
      document.removeEventListener('shopify:section:select', this.onSectionSelect);
      document.removeEventListener('shopify:section:deselect', this.onSectionDeselect);
      this.resizeObserver?.disconnect();
      window.clearInterval(this.countdownInterval);
      window.cancelAnimationFrame(this.scrollFrame);
      this.setBodyState(false);
    }

    onScroll() {
      if (this.scrollFrame) return;
      this.scrollFrame = window.requestAnimationFrame(() => {
        this.scrollFrame = null;
        this.updateVisibility();
      });
    }

    onResize() {
      this.updateVisibility();
      this.updateHeight();
    }

    onSectionSelect(event) {
      if (event.detail.sectionId !== this.sectionId) return;
      this.editorPreview = true;
      this.updateVisibility();
    }

    onSectionDeselect(event) {
      if (event.detail.sectionId !== this.sectionId) return;
      this.editorPreview = false;
      this.updateVisibility();
    }

    getRollingDeadline() {
      const duration = this.countdownDuration * 60 * 1000;
      const now = Date.now();
      const fallback = now + duration;
      const storageKey = this.dataset.storageKey;

      if (!storageKey) return fallback;

      try {
        const storedDeadline = Number.parseInt(window.localStorage.getItem(storageKey) || '', 10);
        if (Number.isFinite(storedDeadline) && storedDeadline > now) return storedDeadline;
        window.localStorage.setItem(storageKey, String(fallback));
      } catch (error) {
        return fallback;
      }

      return fallback;
    }

    updateCountdown() {
      const remaining = this.deadline - Date.now();

      if (remaining <= 0) {
        window.clearInterval(this.countdownInterval);
        this.handleExpiry();
        return;
      }

      const totalSeconds = Math.floor(remaining / 1000);
      const days = Math.floor(totalSeconds / 86400);
      const hours = Math.floor((totalSeconds % 86400) / 3600);
      const minutes = Math.floor((totalSeconds % 3600) / 60);
      const seconds = totalSeconds % 60;
      const time = [hours, minutes, seconds].map((value) => String(value).padStart(2, '0')).join(':');

      if (this.countdownOutput) {
        this.countdownOutput.textContent = days > 0 ? `${days}d ${time}` : time;
      }

      if (this.countdownAccessible) {
        const dayText = days > 0 ? `${days} ${days === 1 ? 'day' : 'days'}, ` : '';
        this.countdownAccessible.textContent = `${dayText}${hours} hours, ${minutes} minutes and ${seconds} seconds remaining`;
      }
    }

    handleExpiry() {
      this.isExpired = true;

      if (this.expiryBehaviour === 'replace') {
        this.classList.add('is-expired');
        this.isEligible = true;
      } else {
        this.isEligible = false;
      }

      this.updateVisibility();
    }

    updateVisibility() {
      const visibleForViewport = window.getComputedStyle(this).display !== 'none';
      const afterTrigger = window.scrollY >= this.scrollTrigger;
      const shouldShow = this.isEligible && visibleForViewport && (afterTrigger || this.editorPreview);

      this.urgencyBar.classList.toggle('is-visible', shouldShow);
      this.urgencyBar.setAttribute('aria-hidden', String(!shouldShow));
      this.urgencyBar.toggleAttribute('inert', !shouldShow);
      this.setBodyState(shouldShow);
    }

    updateHeight() {
      if (!this.urgencyBar.classList.contains('is-visible')) return;
      /* AA fix 2026-08-09: aa-cro.css applies body{zoom:1.1/1.25} on desktop.
         getBoundingClientRect() returns VISUAL px, but this CSS var is consumed
         inside the zoomed body (header-sticky inset), where the browser scales
         it AGAIN — so the header pinned (zoom-1)×barHeight too low, leaving a
         gap between the urgency bar and the nav. Divide by the effective body
         zoom so the stored value is in CSS px and the header sits flush. */
      const zoom = parseFloat(window.getComputedStyle(document.body).zoom) || 1;
      const height = (this.urgencyBar.getBoundingClientRect().height / zoom).toFixed(2);
      document.documentElement.style.setProperty('--neat-urgency-height', `${height}px`);
    }

    setBodyState(isVisible) {
      if (isVisible) {
        document.body.classList.add('neat-urgency-visible');
        document.body.dataset.neatUrgencyOwner = this.sectionId;
        window.requestAnimationFrame(() => this.updateHeight());
        return;
      }

      if (document.body.dataset.neatUrgencyOwner === this.sectionId) {
        document.body.classList.remove('neat-urgency-visible');
        delete document.body.dataset.neatUrgencyOwner;
        document.documentElement.style.removeProperty('--neat-urgency-height');
      }
    }
  }

  customElements.define(elementName, NeatAnnouncementUrgency);
})();
